import React, { useCallback, useEffect, useRef, useState } from "react";
import { resumableFiles, Uploader } from "..";
import { Alert } from "@/utils/ui/Alert/Alert";
import { callRegisterMediaOnCms } from "@/store/upload/upload.action";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "@/store/index";
import { addMediaAsset, setMediaDetails } from "@/store/media/media.slice";
import { AssetItem } from "@/database";
import { updateIsThumbnailUploading } from "@/store/media/media.action";
import { updateCurrentImageId } from "@/store/media/media.action";
import ImageCropper from "./ImageCropper";
import {
  updateFileProgress,
  addFile,
  setUploadedSuccessfully,
} from "@/store/upload/upload.slice";
import { Upload } from "lucide-react";

interface ImageState {
  url: string;
  id: string;
}

const ThumbnailSelectionModal = ({
  handleThumbnailSelected,
  images,
  callRegisterMediaOnCms,
  currentMedia,
  addMediaAsset,
  type,
  isThumbnailUploading,
  updateIsThumbnailUploading,
  currentThumbnailId,
  mediaAssets,
  updateCurrentImageId,
  currentImageId,
  aspectRatio,
  onCropRequest,
  onCropperClose,
  isRequiredThumb,
  onCropperSkipReady,
  setMediaDetails,
}: ThumbnailSelectionModalProps) => {
  const [allImages, setAllImages] = useState<ImageState[]>(images);
  const [selectedImageId, setSelectedImageId] = useState<string>(
    currentThumbnailId || ""
  );
  const [showCropper, setShowCropper] = useState<boolean>(false);
  const [imageToCrop, setImageToCrop] = useState<string>("");
  const [croppedImageFile, setCroppedImageFile] = useState<File | null>(null);
  const [pendingFileData, setPendingFileData] = useState<any>(null);
  const pendingUploadCallbackRef = useRef<any>(null);
  const [aspectRatioUpdateTrigger, setAspectRatioUpdateTrigger] =
    useState<number>(0);
  const [calculatingImages, setCalculatingImages] = useState<Set<string>>(
    new Set()
  );
  const [triggerCrop, setTriggerCrop] = useState<boolean>(false);

  useEffect(() => {
    if (triggerCrop) {
      setTriggerCrop(false);
    }
  }, [triggerCrop]);

  useEffect(() => {
    if (!showCropper && onCropperClose) {
      onCropperClose();
    }
  }, [showCropper, onCropperClose]);
  const ASPECT_RATIO_TOLERANCE = 0.3;

  const isAspectRatioWithinTolerance = (
    actual: number,
    expected: number,
    tolerance = ASPECT_RATIO_TOLERANCE
  ): boolean => {
    return Math.abs(actual - expected) <= tolerance;
  };

  const calculateIntrinsicAspectRatio = (
    imageElement: HTMLImageElement
  ): number => {
    const naturalWidth = imageElement.naturalWidth;
    const naturalHeight = imageElement.naturalHeight;

    if (naturalWidth === 0 || naturalHeight === 0) {
      return 0;
    }

    const aspectRatio = naturalWidth / naturalHeight;
    const multipliedRatio = aspectRatio * 10;
    const flooredRatio = Math.floor(multipliedRatio);

    return flooredRatio;
  };

  const saveCalculatedAspectRatio = (imageId: string, aspectRatio: number) => {
    const aspectRatios = JSON.parse(
      localStorage.getItem("imageAspectRatios") || "{}"
    );
    aspectRatios[imageId] = aspectRatio;
    localStorage.setItem("imageAspectRatios", JSON.stringify(aspectRatios));
    setAspectRatioUpdateTrigger((prev) => prev + 1);
  };

  const getImageUrl = (asset: AssetItem, imageId: string): string => {
    const useOriginalImage = type === "podcast" || type === "portrait";
    if (useOriginalImage) {
      return `${asset?.baseurl}/${asset?.id}${
        currentMedia?.source === "dbsync" ? ".jpg" : ".jpg"
      }`;
    } else {
      return `${asset?.baseurl}/${asset?.id}${
        currentMedia?.source === "dbsync" ? ".jpg" : ".jpg"
      }`; //as it converts to 16/9 ratio
    }
  };

  const getSavedAspectRatio = (imageId: string): number => {
    const aspectRatios = JSON.parse(
      localStorage.getItem("imageAspectRatios") || "{}"
    );
    return aspectRatios[imageId] || 0;
  };

  const shouldDisableImage = (imageId: string): boolean => {
    if (imageId === selectedImageId) {
      return false;
    }
    if (calculatingImages.has(imageId)) {
      return false;
    }
    if (isRequiredThumb) {
      return false;
    }
    const aspectRatio = getSavedAspectRatio(imageId);

    switch (type) {
      case "thumb":
        return aspectRatio !== 17;
      case "social":
        return aspectRatio !== 17;
      case "portrait":
        return aspectRatio !== 5;
      case "podcast":
        return aspectRatio !== 10;
      default:
        return false;
    }
  };

  const getDisabledState = useCallback(
    (imageId: string): boolean => {
      return shouldDisableImage(imageId);
    },
    [aspectRatioUpdateTrigger, type]
  );

  const getImageStyle = (imageId: string) => {
    const isDisabled = getDisabledState(imageId);
    const isSelected = imageId === selectedImageId;

    return {
      height: "90px",
      width: "160px",
      border: isSelected ? "2px solid #0d6efd" : "1px solid #dee2e6",
      borderRadius: "4px",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: isDisabled ? 0.5 : 1,
      cursor: isDisabled ? "not-allowed" : "pointer",
      position: "relative" as const,
    };
  };

  const getExpectedAspectRatio = (): number => {
    switch (type) {
      case "thumb":
        return 17; // 16:9 ratio (floored from 1.777 * 10)
      case "social":
        return 17; // 16:9 ratio
      case "portrait":
        return 5; // 9:16 ratio (floored from 0.5625 * 10)
      case "podcast":
        return 10; // 1:1 ratio
      default:
        return 0;
    }
  };

  const checkAspectRatioAndShowCropper = (
    imageDataUrl: string,
    file: any,
    data: any,
    cb: any
  ) => {
    const img = new Image();
    img.onload = () => {
      const uploadedImageRatio = calculateIntrinsicAspectRatio(img);
      const expectedRatio = getExpectedAspectRatio();

      // If aspect ratios match, skip cropper and upload directly
      if (isAspectRatioWithinTolerance(uploadedImageRatio, expectedRatio)) {
        setShowCropper(false);
        updateIsThumbnailUploading && updateIsThumbnailUploading(true);
        updateCurrentImageId(null);

        // Use the callback directly with the query and token registration
        const resumableFile = {
          ...file,
          isShorts: false,
          type: "thumb",
          name: "thumb",
          id: currentMedia.id,
          damid: currentMedia.damid,
          cdn: currentMedia.cdn?.map((item) => ({ id: item.id })),
          business: currentMedia.business,
        };

        callRegisterMediaOnCms(resumableFile, (response: any) => {
          if (response && response.status === 200) {
            const token = response.data.jwt;
            const query = {
              entity: currentMedia.id,
              key: "thumb",
            };
            if (cb) {
              cb(query, token);
            }
          } else {
            updateIsThumbnailUploading && updateIsThumbnailUploading(false);
          }
        });
      } else {
        // Aspect ratios don't match, show cropper
        setImageToCrop(imageDataUrl);
        setShowCropper(true);
        pendingUploadCallbackRef.current = cb;
        setPendingFileData({ file, data });
      }
    };
    img.onerror = () => {
      // If image fails to load, show cropper as fallback
      setImageToCrop(imageDataUrl);
      setShowCropper(true);
      pendingUploadCallbackRef.current = cb;
      setPendingFileData({ file, data });
    };
    img.src = imageDataUrl;
  };

  const onFileEvent = (event: string, data: any, cb?: any): any => {
    if (event === "fileAdded") {
      const file = data.file;

      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target) {
          checkAspectRatioAndShowCropper(
            e.target.result as string,
            file,
            data,
            cb
          );
        }
      };
      reader.readAsDataURL(file.file);
    } else if (event === "fileProgress") {
      const file = data.file;
      const progress = Math.round(file.progress(false) * 1000) / 10;
    } else if (event === "fileSuccess") {
      const response = JSON.parse(data.response);

      setSelectedImageId(response?.data?.id);
      handleThumbnailSelected({
        url: response?.data?.url,
        id: response?.data?.id,
      });

      if (response) {
        const newAsset: AssetItem = {
          name: response.data.name,
          id: response.data.id,
          pid: response.data.pid,
          baseurl: response.data.baseurl,
          type: response.data.type,
          nid: response.data.nid,
          ct: response.data.ct,
          mt: response.data.mt,
          user: response.data.user,
          status: response.data.status,
        };

        addMediaAsset({ asset: newAsset });
        updateCurrentImageId(response.data.id);

        if (!currentMedia?.images?.base && response.data.baseurl) {
          setMediaDetails({
            data: {
              media: {
                ...currentMedia,
                images: {
                  ...currentMedia.images,
                  base: response.data.baseurl,
                },
              },
            },
          });
        }

        updateIsThumbnailUploading && updateIsThumbnailUploading(false);
      }
    } else if (event === "fileError") {
      const { file, error } = data;
      updateIsThumbnailUploading && updateIsThumbnailUploading(false);
      setPendingFileData(null);
      pendingUploadCallbackRef.current = null;
    }
  };

  const handleCropComplete = (croppedImageBlob: Blob) => {
    const croppedFile = new File([croppedImageBlob], "cropped-thumbnail.jpg", {
      type: "image/jpeg",
    });
    setCroppedImageFile(croppedFile);
    setShowCropper(false);
    updateIsThumbnailUploading && updateIsThumbnailUploading(true);
    updateCurrentImageId(null);

    const originalFileData = pendingFileData;
    const resumableFile = {
      ...originalFileData.file,
      file: croppedFile,
      fileName: "cropped-thumbnail.jpg",
      uniqueIdentifier: `thumb-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      size: croppedFile.size,
      isShorts: false,
      type: "thumb",
      name: "thumb",
      id: currentMedia.id,
      damid: currentMedia.damid,
      cdn: currentMedia.cdn?.map((item) => ({ id: item.id })),
      business: currentMedia.business,
    };

    callRegisterMediaOnCms(resumableFile, (response: any) => {
      if (response && response.status === 200) {
        const token = response.data.jwt;
        const query = {
          entity: currentMedia.id,
          key: "thumb",
        };
        if (pendingUploadCallbackRef.current) {
          const originalFileData = pendingFileData;
          const originalFile = originalFileData.file;
          const originalResumableFile =
            resumableFiles[originalFile.uniqueIdentifier];
          if (originalResumableFile) {
            originalResumableFile.file = croppedFile;
            originalResumableFile.fileName = "cropped-thumbnail.jpg";
            originalResumableFile.size = croppedFile.size;
          }
          pendingUploadCallbackRef.current(query, token);
        } else {
          console.error("No pendingUploadCallback found!");
          updateIsThumbnailUploading && updateIsThumbnailUploading(false);
        }
        setPendingFileData(null);
      } else {
        updateIsThumbnailUploading && updateIsThumbnailUploading(false);
        setPendingFileData(null);
      }
    });
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop("");
    setCroppedImageFile(null);
    updateIsThumbnailUploading && updateIsThumbnailUploading(false);
    setPendingFileData(null);
    pendingUploadCallbackRef.current = null;
    if (onCropperClose) {
      onCropperClose();
    }
  };

  const handleSkipCrop = useCallback(() => {
    const originalFileData = pendingFileData;
    if (!originalFileData || !pendingUploadCallbackRef.current) return;

    setShowCropper(false);
    setImageToCrop("");
    setCroppedImageFile(null);
    updateIsThumbnailUploading && updateIsThumbnailUploading(true);
    updateCurrentImageId(null);

    const resumableFile = {
      ...originalFileData.file,
      isShorts: false,
      type: "thumb",
      name: "thumb",
      id: currentMedia.id,
      damid: currentMedia.damid,
      cdn: currentMedia.cdn?.map((item) => ({ id: item.id })),
      business: currentMedia.business,
      isSkipped: true,
    };

    callRegisterMediaOnCms(resumableFile, (response: any) => {
      if (response && response.status === 200) {
        const token = response.data.jwt;
        const query = {
          entity: currentMedia.id,
          key: "thumb",
        };
        if (pendingUploadCallbackRef.current) {
          pendingUploadCallbackRef.current(query, token);
        }
        setPendingFileData(null);
        pendingUploadCallbackRef.current = null;
      } else {
        updateIsThumbnailUploading && updateIsThumbnailUploading(false);
        setPendingFileData(null);
      }
    });
  }, [
    pendingFileData,
    currentMedia.id,
    currentMedia.damid,
    currentMedia.cdn,
    currentMedia.business,
    callRegisterMediaOnCms,
    updateIsThumbnailUploading,
  ]);

  useEffect(() => {
    if (onCropperSkipReady) {
      onCropperSkipReady(showCropper ? handleSkipCrop : null);
    }
  }, [showCropper, onCropperSkipReady, handleSkipCrop]);

  const handleSelectedImage = (image: string, id: string) => {
    handleThumbnailSelected({ url: image, id: id });
  };

  useEffect(() => {
    return () => {
      updateIsThumbnailUploading && updateIsThumbnailUploading(false);
      setPendingFileData(null);
      pendingUploadCallbackRef.current = null;
    };
  }, [updateIsThumbnailUploading]);

  useEffect(() => {
    const updatedImages = [...images];
    mediaAssets.forEach((asset: AssetItem) => {
      if (asset.name === "thumb" && asset.status === 3) {
        const imageExists = updatedImages?.some((img) => img?.id === asset?.id);
        if (!imageExists) {
          updatedImages.push({
            // url: getThumbnailUrlUtil(asset, asset.id, currentMedia?.source),
            url: getImageUrl(asset, asset.id),
            id: asset?.id,
          });
        }
      }
    });
    if (currentImageId) {
      const imageExists = updatedImages.some(
        (img) => img?.id === currentImageId
      );
      if (!imageExists) {
        const newAsset = mediaAssets.find(
          (asset: AssetItem) => asset?.id === currentImageId
        );
        if (newAsset) {
          updatedImages.push({
            url: getImageUrl(newAsset, currentImageId),
            // url: getThumbnailUrlUtil(newAsset, newAsset.id, currentMedia?.source),
            id: currentImageId,
          });
        }
      }
    }
    const uniqueImages = Array.from(
      new Map(updatedImages.map((item) => [item?.id, item])).values()
    );
    setAllImages(uniqueImages);
  }, [mediaAssets, currentImageId, images]);

  useEffect(() => {
    allImages.forEach((image) => {
      const savedRatio = getSavedAspectRatio(image.id);
      if (savedRatio === 0) {
        setCalculatingImages((prev) => new Set(prev).add(image.id));
        const immediateRatio = 17;
        saveCalculatedAspectRatio(image.id, immediateRatio);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const actualRatio = calculateIntrinsicAspectRatio(img);
          saveCalculatedAspectRatio(image.id, actualRatio);
          setCalculatingImages((prev) => {
            const newSet = new Set(prev);
            newSet.delete(image.id);
            return newSet;
          });
        };
        img.onerror = () => {
          setCalculatingImages((prev) => {
            const newSet = new Set(prev);
            newSet.delete(image.id);
            return newSet;
          });
        };
        img.src = image.url;
      }
    });
  }, [allImages]);

  // Separate useEffect for sorting to prevent infinite loop
  useEffect(() => {
    const sortedImages = [...allImages].sort((a, b) => {
      const aDisabled = getDisabledState(a.id);
      const bDisabled = getDisabledState(b.id);

      if (aDisabled === bDisabled) {
        return 0; // Keep original order if both have same disabled state
      }

      return aDisabled ? 1 : -1; // Put non-disabled (false) first
    });

    // Only update if the order actually changed
    const currentOrder = allImages.map((img) => img.id).join(",");
    const sortedOrder = sortedImages.map((img) => img.id).join(",");

    if (currentOrder !== sortedOrder) {
      setAllImages(sortedImages);
    }
  }, [allImages, getDisabledState]);

  const getAspectRatioRequirement = (): string => {
    switch (type) {
      case "thumb":
        return "16:9 aspect ratio";
      case "social":
        return "16:9 aspect ratio";
      case "portrait":
        return "9:16 aspect ratio";
      case "podcast":
        return "1:1 aspect ratio";
      default:
        return "";
    }
  };

  const getThumbnailTypeName = (): string => {
    switch (type) {
      case "thumb":
        return "media thumbnails";
      case "social":
        return "social thumbnails";
      case "portrait":
        return "portrait thumbnails";
      case "podcast":
        return "podcast thumbnails";
      default:
        return "thumbnails";
    }
  };

  if (showCropper) {
    return (
      <ImageCropper
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
        onCancel={handleCropCancel}
        aspectRatio={aspectRatio}
        triggerCrop={triggerCrop}
        onCropRequest={() => {
          if (onCropRequest) {
            const triggerCrop = () => {
              setTriggerCrop(true);
            };
            onCropRequest(triggerCrop);
          }
        }}
      />
    );
  }

  return (
    <div>
      {type &&
        ["thumb", "social", "portrait", "podcast"].includes(type) &&
        !isRequiredThumb && (
          <Alert variant="warning">
            <i className="bi bi-info-circle me-2"></i>
            Only images with {getAspectRatioRequirement()} are available for{" "}
            {getThumbnailTypeName()}.
          </Alert>
        )}
      <div className="d-flex gap-2 flex-wrap">
        <Uploader
          id={"Thumb"}
          onFileEvent={onFileEvent}
          datatype={"IMAGE"}
          updateIsThumbnailUploading={updateIsThumbnailUploading}
          isThumbnailUploading={isThumbnailUploading}
        >
          <div
            className="border border-rounded d-flex position-relative catRow"
            style={{
              width: "160px",
              height: "90px",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              textAlign: "center",
              transition: "border 0.3s ease",
            }}
          >
            <div className="d-flex flex-column gap-2 align-items-center">
              <Upload />
              <span>CUSTOM UPLOAD</span>
            </div>
            {isThumbnailUploading && (
              <div
                className="d-flex align-items-center justify-content-center h-100 w-100 position-absolute top-50 start-50 translate-middle"
                id="loader"
                style={{ backgroundColor: "rgba(0,0,0,0.2" }}
              >
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
          </div>
        </Uploader>
        {allImages.map((image) => (
          <div
            key={image.id}
            style={getImageStyle(image.id)}
            className="list-group-item pointer position-relative"
            onClick={() => {
              if (!getDisabledState(image.id)) {
                handleSelectedImage(image.url, image.id);
                setSelectedImageId(image.id);
              }
            }}
          >
            <img
              src={image.url}
              alt=""
              style={{
                height: "90px",
                width: "160px",
                objectFit: "contain",
              }}
              onLoad={(e) => {
                const imgElement = e.target as HTMLImageElement;
                const aspectRatio = calculateIntrinsicAspectRatio(imgElement);
                saveCalculatedAspectRatio(image.id, aspectRatio);
              }}
            />
            {image.id === selectedImageId && (
              <div className="position-absolute top-0 end-0 p-1">
                <i
                  className="bi bi-check-circle-fill fs-4 text-white"
                  style={{
                    textShadow:
                      "0 0 2px rgba(0, 0, 0, 0.95), 0 0 6px rgba(0, 0, 0, 0.75)",
                  }}
                ></i>
              </div>
            )}
            {getDisabledState(image.id) && (
              <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-75">
                <div className="text-light text-center small">
                  <i className="bi bi-lock-fill d-block mb-1"></i>
                  <span>Incompatible ratio</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  currentMedia: state.media.mediaDetails.media,
  isThumbnailUploading: state.media.isThumbnailUploading,
  mediaAssets: state.media.mediaAssets.assets,
  currentImageId: state.media.currentImageId,
});

const mapDispatch = {
  callRegisterMediaOnCms,
  addMediaAsset,
  updateIsThumbnailUploading,
  updateCurrentImageId,
  updateFileProgress,
  addFile,
  setUploadedSuccessfully,
  setMediaDetails,
};

interface IThumbnailSelectionModalProps {
  images: ImageState[];
  handleThumbnailSelected: (data: { url: string; id: string }) => void;
  closeModal: () => void;
  type?: string;
  currentThumbnailId?: string;
  handleFlag: () => void;
  aspectRatio?: number;
  onCropRequest?: (cropFn: () => void) => void;
  onCropperClose?: () => void;
  onCropperSkipReady?: (skipFn: (() => void) | null) => void;
  isWizard?: boolean;
  isRequiredThumb?: boolean;
}

const connector = connect(mapState, mapDispatch);

type ThumbnailSelectionModalProps = ConnectedProps<typeof connector> &
  IThumbnailSelectionModalProps;

export default connector(ThumbnailSelectionModal);
